import { useCallback, useEffect, useState } from 'react';
import { getRouteStackPath } from '@portal/config/routes';
import { PixActions } from '@portal/actions';
import { encrypt } from '@portal/repositories/securitySdkConfig';
import { PixType } from '@portal/enum/pix';
import { PixStatus } from '@portal/enum/PixStatus';
import { PendingPixKeyPortability } from '@portal/models/module';
import useReduxState from '@portal/hooks/useReduxState';
import MessageService from '@portal/services/message';
import { translate } from '@portal/services/i18n';
import { useDispatch } from 'react-redux';
import { useNavigation } from '@portal/hooks/useNavigation';
import { selectPortabilityEnabled } from '@portal/utils/accountSettings';

export interface UsePixPortabilityReturn {
  openPixPortabilityModal: boolean;
  setOpenPixPortabilityModal: (v: boolean) => void;
  pendingPortability: PendingPixKeyPortability | null;
  setPendingPortability: (v: PendingPixKeyPortability | null) => void;
  pendingPortabilityTagModalOpen: boolean;
  setPendingPortabilityTagModalOpen: (v: boolean) => void;
  pendingPortabilityKeyValue: string;
  setPendingPortabilityKeyValue: (v: string) => void;
  pendingPortabilityStatus: PixStatus | undefined;
  setPendingPortabilityStatus: (v: PixStatus | undefined) => void;
  confirmPortabilityModalOpen: boolean;
  setConfirmPortabilityModalOpen: (v: boolean) => void;
  openKeyPortabilityModal: boolean;
  setOpenKeyPortabilityModal: (v: boolean) => void;
  statusSelected: PixStatus | undefined;
  setStatusSelected: (v: PixStatus | undefined) => void;
  pixKey: string;
  setPixKey: (v: string) => void;
  pixType: PixType | undefined;
  setPixType: (v: PixType | undefined) => void;
  validationModal: boolean;
  setValidationModal: (v: boolean) => void;
  validationCode: string;
  setValidationCode: (v: string) => void;
  pendingPinAction: 'confirm_token' | 'resend' | null;
  setPendingPinAction: (v: 'confirm_token' | 'resend' | null) => void;
  handleModal: (
    status: PixStatus,
    pixKeyValue: string,
    pixType: PixType
  ) => void;
  handleConfirmTrue: () => void;
  handleConfirmFalse: () => void;
  handleValidationPin: (pin: string) => Promise<void>;
  handleResendCode: () => void;
  confirmPortabilityWithPin: (
    pin: string,
    key: string,
    pixTypeValue: number,
    token?: string,
    isPortability?: boolean
  ) => Promise<void>;
  requestPortabilityCodeWithPin: (
    pin: string,
    key: string,
    pixTypeValue: number
  ) => Promise<models.RegisterPixKeyDetails | undefined>;
  isPortabilityEnabled: boolean;
}

export function usePixPortability(): UsePixPortabilityReturn {
  const dispatch = useDispatch();
  const navigate = useNavigation();
  const {
    pix: { portabilitySuccess, cancelPortabilitySuccess },
    account: { config },
  } = useReduxState();
  const isPortabilityEnabled = selectPortabilityEnabled(config);

  const [openPixPortabilityModal, setOpenPixPortabilityModal] = useState(false);
  const [pendingPortability, setPendingPortability] =
    useState<PendingPixKeyPortability | null>(null);
  const [pendingPortabilityTagModalOpen, setPendingPortabilityTagModalOpen] =
    useState(false);
  const [pendingPortabilityKeyValue, setPendingPortabilityKeyValue] =
    useState('');
  const [pendingPortabilityStatus, setPendingPortabilityStatus] = useState<
    PixStatus | undefined
  >();
  const [confirmPortabilityModalOpen, setConfirmPortabilityModalOpen] =
    useState(false);
  const [openKeyPortabilityModal, setOpenKeyPortabilityModal] = useState(false);
  const [statusSelected, setStatusSelected] = useState<PixStatus | undefined>();
  const [pixKey, setPixKey] = useState('');
  const [pixType, setPixType] = useState<PixType | undefined>(-1 as PixType);
  const [validationModal, setValidationModal] = useState(false);
  const [validationCode, setValidationCode] = useState('');
  const [pendingPinAction, setPendingPinAction] = useState<
    'confirm_token' | 'resend' | null
  >(null);

  const handleModal = useCallback(
    (status: PixStatus, pixKeyValue: string, pixTypeValue: PixType) => {
      if (status === PixStatus.CLEARING_REGISTRATION_PENDING) return;

      if (status === PixStatus.IDENTITY_VALIDATION_PENDING) {
        setValidationModal(true);
        setStatusSelected(status);
        setPixKey(pixKeyValue);
        setPixType(pixTypeValue);
        return;
      }

      if (status === PixStatus.PENDING_VALIDATION_COMPLETE_CLAIM) {
        setOpenKeyPortabilityModal(true);
        setStatusSelected(status);
        setPixKey(pixKeyValue);
        setPixType(pixTypeValue);
        return;
      }

      if (status === PixStatus.AWAITING_RETURN_PSP_DONOR) {
        setConfirmPortabilityModalOpen(true);
        setStatusSelected(status);
        setPixKey(pixKeyValue);
        setPixType(pixTypeValue);
        return;
      }

      if (status === PixStatus.CLAIM_AWAITING_RETURN_PSP_DONOR) {
        setPendingPortabilityTagModalOpen(true);
        setPendingPortabilityKeyValue(pixKeyValue);
        setPendingPortabilityStatus(status);
        return;
      }

      setStatusSelected(status);
      setPixKey(pixKeyValue);
      setPixType(pixTypeValue);
    },
    []
  );

  const handleConfirmTrue = useCallback(() => {
    if (statusSelected === PixStatus.AWAITING_RETURN_PSP_DONOR) {
      return;
    }

    if (statusSelected === PixStatus.PENDING_VALIDATION_COMPLETE_CLAIM) {
      setPendingPinAction('confirm_token');
      setValidationModal(true);
      return;
    }

    if (statusSelected === PixStatus.IDENTITY_VALIDATION_PENDING) {
      setPendingPinAction('confirm_token');
      setValidationModal(true);
      return;
    }

    if (statusSelected === PixStatus.USER_CONFIRMATION_PENDING_PORTABILITY) {
      dispatch(PixActions.portabilityResponse(pixKey, false));
      return;
    }
  }, [dispatch, statusSelected, pixKey]);

  const handleConfirmFalse = useCallback(() => {
    switch (statusSelected) {
      case PixStatus.USER_CONFIRMATION_PENDING_PORTABILITY:
        dispatch(PixActions.portabilityResponse(pixKey, true));
        break;

      case PixStatus.AWAITING_RETURN_PSP_DONOR:
        dispatch(PixActions.deletePortability(pixKey));
        break;

      case PixStatus.IDENTITY_VALIDATION_PENDING:
      case PixStatus.PENDING_VALIDATION_COMPLETE_CLAIM:
        break;

      default:
        break;
    }
  }, [dispatch, statusSelected, pixKey]);

  const handleResendCode = useCallback(() => {
    setPendingPinAction('resend');
    setValidationModal(true);
  }, []);

  const confirmPortabilityWithPin = useCallback(
    async (
      pin: string,
      key: string,
      pixTypeValue: number,
      token?: string,
      isPortability = true
    ) => {
      if (isPortability && !isPortabilityEnabled) {
        MessageService.error(translate('ERRORS.PORTABILITY_DISABLED'));
        return;
      }
      const encryptedPin = await encrypt(pin);
      const requestData: models.RegisterPixKey = {
        authenticate: { pin: encryptedPin },
        key,
        pixType: pixTypeValue,
        createReivindication: isPortability,
      };
      if (token) requestData.token = token;
      await dispatch(PixActions.confirmPortability(requestData));
    },
    [dispatch, isPortabilityEnabled]
  );

  const requestPortabilityCodeWithPin = useCallback(
    async (
      pin: string,
      key: string,
      pixTypeValue: number
    ): Promise<models.RegisterPixKeyDetails | undefined> => {
      if (!isPortabilityEnabled) {
        MessageService.error(translate('ERRORS.PORTABILITY_DISABLED'));
        return undefined;
      }
      const encryptedPin = await encrypt(pin);
      const requestData: models.RegisterPixKey = {
        authenticate: { pin: encryptedPin },
        key,
        pixType: pixTypeValue,
        createReivindication: true,
      };
      try {
        const payload = await dispatch(
          PixActions.requestPortabilityCode(requestData)
        );
        return payload as unknown as models.RegisterPixKeyDetails | undefined;
      } catch {
        throw new Error();
      }
    },
    [dispatch, isPortabilityEnabled]
  );

  const handleValidationPin = useCallback(
    async (pin: string) => {
      const encryptedPin = await encrypt(pin);
      const keyForRequest =
        pixType === PixType.PHONE && !pixKey.startsWith('+')
          ? `+55${pixKey.replace(/\D/g, '')}`
          : pixKey;
      const typeNum = Number(pixType);

      if (pendingPinAction === 'confirm_token' && validationCode) {
        if (!isPortabilityEnabled) {
          MessageService.error(translate('ERRORS.PORTABILITY_DISABLED'));
          setPendingPinAction(null);
          setValidationModal(false);
          setValidationCode('');
          return;
        }
        dispatch(
          PixActions.confirmPortability({
            authenticate: { pin: encryptedPin },
            key: keyForRequest,
            pixType: typeNum,
            token: validationCode,
            createReivindication: true,
          })
        );
        setPendingPinAction(null);
        setValidationModal(false);
        setValidationCode('');
        return;
      }

      if (pendingPinAction === 'resend') {
        if (!isPortabilityEnabled) {
          MessageService.error(translate('ERRORS.PORTABILITY_DISABLED'));
          setPendingPinAction(null);
          setValidationModal(false);
          return;
        }
        dispatch(
          PixActions.requestPortabilityCode({
            authenticate: { pin: encryptedPin },
            key: keyForRequest,
            pixType: typeNum,
            createReivindication: true,
          })
        );
        setPendingPinAction(null);
        setValidationModal(false);
        return;
      }

      const requestData: models.RegisterPixKey = {
        authenticate: {
          pin: encryptedPin,
        },
        key: keyForRequest,
        pixType: typeNum,
      };

      dispatch(PixActions.registerPixKey(requestData));
    },
    [
      dispatch,
      pixKey,
      pixType,
      pendingPinAction,
      validationCode,
      isPortabilityEnabled,
    ]
  );

  useEffect(() => {
    if (!cancelPortabilitySuccess) return;

    setTimeout(() => {
      dispatch(PixActions.getAllPixKeys());
      navigate(getRouteStackPath('PIX', 'PIX'));
    }, 3000);
  }, [cancelPortabilitySuccess, dispatch, navigate]);

  useEffect(() => {
    if (!portabilitySuccess) return;

    setTimeout(() => {
      dispatch(PixActions.getAllPixKeys());
      navigate(getRouteStackPath('PIX', 'PIX'));
    }, 3000);
  }, [portabilitySuccess, dispatch, navigate]);

  return {
    openPixPortabilityModal,
    setOpenPixPortabilityModal,
    pendingPortability,
    setPendingPortability,
    pendingPortabilityTagModalOpen,
    setPendingPortabilityTagModalOpen,
    pendingPortabilityKeyValue,
    setPendingPortabilityKeyValue,
    pendingPortabilityStatus,
    setPendingPortabilityStatus,
    confirmPortabilityModalOpen,
    setConfirmPortabilityModalOpen,
    openKeyPortabilityModal,
    setOpenKeyPortabilityModal,
    statusSelected,
    setStatusSelected,
    pixKey,
    setPixKey,
    pixType,
    setPixType,
    validationModal,
    setValidationModal,
    validationCode,
    setValidationCode,
    pendingPinAction,
    setPendingPinAction,
    handleModal,
    handleConfirmTrue,
    handleConfirmFalse,
    handleValidationPin,
    handleResendCode,
    confirmPortabilityWithPin,
    requestPortabilityCodeWithPin,
    isPortabilityEnabled,
  };
}
